import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import iconImg from "@/assets/icon.png";
import { useBranding } from "@/hooks/useBranding";
import { trackOnboardingStarted } from "@/analytics/analytics";
import OnboardingIntro from "./onboarding/OnboardingIntro";
import OnboardingWizard from "./onboarding/OnboardingWizard";
import type { OnboardingProfile } from "./onboarding/types";

interface OnboardingModalProps {
  isLinked: boolean;
  userType: "player" | "coach";
  onLinkSuccess: () => void | Promise<void>;
  onboardingCompletedAt?: string | null;
  onboardingVersion?: number | null;
  onboardingProgress?: import("./onboarding/types").OnboardingProgress | null;
  isReturningUser?: boolean;
  onLogout?: () => void;
  /** Current user profile — the wizard derives its resume position from it. */
  profile?: OnboardingProfile | null;
}

export default function OnboardingModal({
  isLinked,
  userType,
  onLinkSuccess,
  onboardingCompletedAt,
  onboardingVersion,
  onboardingProgress,
  isReturningUser = false,
  onLogout,
  profile,
}: OnboardingModalProps) {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const icon = branding?.chatIdleIconUrl || iconImg;

  const CURRENT_ONBOARDING_VERSION = 2;
  const onboardingComplete =
    !!onboardingCompletedAt && (onboardingVersion ?? 0) >= CURRENT_ONBOARDING_VERSION;
  const returning = !onboardingComplete && (!!onboardingCompletedAt || isReturningUser);
  // JGS linking is one answer inside onboarding, not a substitute for the full
  // profile, fit, and recruiting-progress flow.
  const shouldOpen = userType === "player" && !onboardingComplete;
  const [isOpen, setIsOpen] = useState(shouldOpen);
  const [flowDone, setFlowDone] = useState(false);

  // Everyone sees the appropriate welcome screen before their first v2 step.
  // Only a genuinely in-progress v2 flow resumes directly in the wizard.
  // This matters for imported players whose basic profile fields may already
  // be populated even though they have never started golfrecruit.ai onboarding.
  const hasCurrentProgress = onboardingProgress?.version === CURRENT_ONBOARDING_VERSION;
  const [showIntro, setShowIntro] = useState(returning || !hasCurrentProgress);

  useEffect(() => {
    if (isOpen && onboardingComplete) setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingComplete]);

  useEffect(() => {
    if (isOpen) trackOnboardingStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!shouldOpen && !isOpen) return null;

  const handleLinkSuccess = async (_profileId: string) => {
    await onLinkSuccess();
  };

  const handleComplete = async () => {
    await onLinkSuccess();
    setIsOpen(false);
  };

  const handleSaveAndExit = () => {
    setIsOpen(false);
    navigate("/");
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open && flowDone) handleComplete();
      }}
    >
      <DialogContent
        className={
          "block h-dvh w-screen max-w-none overflow-hidden rounded-none border-0 p-0 sm:rounded-none " +
          (flowDone ? "" : "[&>button:last-child]:hidden")
        }
        onInteractOutside={event => {
          if (!flowDone) event.preventDefault();
        }}
        onEscapeKeyDown={event => {
          if (!flowDone) event.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">Welcome to golfrecruit.ai</DialogTitle>
        <DialogDescription className="sr-only">
          Build a personalized college golf recruiting plan.
        </DialogDescription>

        {showIntro ? (
          <OnboardingIntro
            onStart={() => setShowIntro(false)}
            onLogout={onLogout}
            iconUrl={icon as string}
            returning={returning}
          />
        ) : (
          <OnboardingWizard
            profile={profile ?? { isLinked }}
            onLinkSuccess={handleLinkSuccess}
            onComplete={handleComplete}
            onExit={returning ? onLogout : handleSaveAndExit}
            exitLabel={returning ? "Log out" : "Save & exit"}
            onboardingVersion={CURRENT_ONBOARDING_VERSION}
            progress={onboardingProgress}
            forceReview={returning}
            onFlowDone={() => {
              setFlowDone(true);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
